import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { useLead } from "../context/LeadContext";
import { SERVICES, waLink, telLink, PROCESS } from "../lib/data";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openLead } = useLead();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!service) navigate("/services");
  }, [slug]);

  if (!service) return null;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page" className="pt-20">
      {/* HERO */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img loading="lazy" decoding="async" src={service.hero} alt={service.title} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian-900/85 to-obsidian-900" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <Link to="/services" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold text-sm mb-6" data-testid="back-to-services">
            <Icons.ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center mb-5">
            <Icon name={service.icon} className="w-7 h-7 text-obsidian-900" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white max-w-3xl">{service.title}</h1>
          <p className="text-gold font-medium mt-2">{service.tagline}</p>
          <p className="text-slate-300 mt-4 max-w-2xl text-lg">{service.desc}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={() => openLead({ source: `service-${slug}`, loan_type: service.title })}
              data-testid="service-apply-button"
              className="gold-gradient text-obsidian-900 font-bold h-12 px-7 rounded-full">
              Apply for {service.title}
            </Button>
            <a href={waLink(`Hi, I'm interested in ${service.title}`)} target="_blank" rel="noreferrer nofollow">
              <Button className="bg-approval hover:bg-approval-dark text-white font-bold h-12 px-6 rounded-full">
                <Icons.MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Key Highlights</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {service.points.map((p) => (
                <div key={p} className="card-dark rounded-xl p-5 flex items-start gap-3">
                  <Icons.CheckCircle2 className="w-5 h-5 text-approval shrink-0 mt-0.5" />
                  <span className="text-slate-200 text-sm">{p}</span>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-2xl font-bold text-white mt-12 mb-6">How It Works</h2>
            <div className="space-y-4">
              {PROCESS.map((p) => (
                <div key={p.step} className="flex gap-4 items-start">
                  <div className="font-mono-fin text-xl font-extrabold text-gold w-10 shrink-0">{p.step}</div>
                  <div>
                    <h3 className="font-semibold text-white">{p.title}</h3>
                    <p className="text-slate-400 text-sm">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="card-dark rounded-2xl p-6 sticky top-24 gold-glow">
              <h3 className="font-heading text-lg font-bold text-white">Need this service?</h3>
              <p className="text-slate-400 text-sm mt-1">Our experts respond within hours.</p>
              <Button onClick={() => openLead({ source: `service-side-${slug}`, loan_type: service.title })}
                className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full mt-5"
                data-testid="service-side-apply">
                Get Free Consultation
              </Button>
              <a href={telLink} className="block">
                <Button variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10 h-11 rounded-full mt-3">
                  <Icons.Phone className="w-4 h-4 mr-2" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-16 bg-obsidian-800 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl font-bold text-white mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`}
                className="group card-dark rounded-2xl p-6 hover:border-gold/40 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gold/12 border border-gold/25 flex items-center justify-center mb-3">
                  <Icon name={s.icon} className="w-5 h-5 text-gold" />
                </div>
                <h3 className="font-heading font-semibold text-white group-hover:text-gold transition-colors">{s.title}</h3>
                <p className="text-slate-400 text-sm mt-1 line-clamp-2">{s.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
